import React, { useState } from 'react';
import { Mail, Calendar, ArrowUpDown, Trash2, Copy, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Contact } from '../../types/contact';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import LastContactDate from './LastContactDate';
import { supabase } from '../../lib/supabase';
import ConfirmationPopup from '../ui/ConfirmationPopup';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useAnonymization } from '../../utils/anonymize';
import { Skeleton } from '../ui/Skeleton';
import { useLocation } from 'react-router-dom';
import NextFollowUp from './NextFollowUp';

interface ContactListViewProps {
  contacts: Contact[];
  onRefresh: () => void;
}

type SortField = 'name' | 'email';
type SortDirection = 'asc' | 'desc';

export default function ContactListView({ contacts, onRefresh }: ContactListViewProps) {
  const { settings, loading: settingsLoading } = useUserSettings();
  const shouldAnonymize = settings?.anonymizing_mode || false;
  const location = useLocation();

  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showAssignCompany, setShowAssignCompany] = useState(false);
  const { options: companyOptions } = useCompanyOptions();
  const [selectedCompany, setSelectedCompany] = useState('');

  const handleAssignCompany = async () => {
    if (!selectedCompany) return;
    
    try {
      const { error: updateError } = await supabase
        .from('nrm_contacts')
        .update({ 
          company_id: selectedCompany,
          updated_at: new Date().toISOString()
        })
        .in('id', Array.from(selectedContacts));

      if (updateError) throw updateError;
      onRefresh();
      setSelectedContacts(new Set());
      setShowAssignCompany(false);
      setSelectedCompany('');
    } catch (err) {
      console.error('Failed to assign company:', err);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const findDuplicates = () => {
    const emailMap = new Map<string, Contact[]>();
    const nameMap = new Map<string, Contact[]>();
    
    contacts.forEach(contact => {
      const emails = [contact.work_email, contact.personal_email].filter(Boolean) as string[];
      emails.forEach(email => {
        if (!emailMap.has(email)) {
          emailMap.set(email, []);
        }
        emailMap.get(email)!.push(contact);
      });

      const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      if (!nameMap.has(fullName)) {
        nameMap.set(fullName, []);
      }
      nameMap.get(fullName)!.push(contact);
    });

    const duplicateContacts = new Set<Contact>();
    emailMap.forEach(contacts => {
      if (contacts.length > 1) {
        contacts.forEach(c => duplicateContacts.add(c));
      }
    });
    nameMap.forEach(contacts => {
      if (contacts.length > 1) {
        contacts.forEach(c => duplicateContacts.add(c));
      }
    });

    return Array.from(duplicateContacts);
  };

  const duplicateContacts = findDuplicates();
  const displayedContacts = showDuplicates ? duplicateContacts : contacts;

  const sortedContacts = [...displayedContacts].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return direction * (`${a.first_name} ${a.last_name}`).localeCompare(`${b.first_name} ${b.last_name}`);
      case 'email':
        const aEmail = a.work_email || a.personal_email || '';
        const bEmail = b.work_email || b.personal_email || '';
        return direction * aEmail.localeCompare(bEmail);
      default:
        return 0;
    }
  });

  const toggleSelectAll = () => {
    if (selectedContacts.size === displayedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(displayedContacts.map(c => c.id)));
    }
  };

  const toggleSelect = (contactId: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('nrm_contacts')
        .delete()
        .in('id', Array.from(selectedContacts));

      if (error) throw error;
      onRefresh();
      setSelectedContacts(new Set());
    } catch (err) {
      console.error('Failed to delete contacts:', err);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className="card overflow-hidden">
        <div className="bg-dark-700 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {selectedContacts.size > 0 && (
              <span>{selectedContacts.size} contact(s) selected</span>
            )}
            <button
              onClick={() => setShowDuplicates(!showDuplicates)}
              className={`btn ${showDuplicates ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2 text-sm`}
            >
              <Copy className="w-4 h-4" />
              {showDuplicates ? 'Show All' : 'Show Duplicates'}
              {!showDuplicates && duplicateContacts.length > 0 && (
                <span className="bg-primary-500 text-white px-2 py-0.5 rounded-full text-xs">
                  {duplicateContacts.length}
                </span>
              )}
            </button>
          </div>
          {selectedContacts.size > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAssignCompany(true)}
                className="btn btn-secondary flex items-center gap-2 text-sm"
              >
                <Building2 className="w-4 h-4" />
                Assign Company
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn btn-secondary flex items-center gap-2 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          )}
        </div>
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedContacts.size === displayedContacts.length}
                  onChange={toggleSelectAll}
                  className="rounded bg-dark-600 border-dark-500"
                />
              </th>
              <th 
                className="px-6 py-3 text-left text-sm font-semibold cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  Name
                  <ArrowUpDown className="w-4 h-4" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-sm font-semibold cursor-pointer"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-2">
                  Email
                  <ArrowUpDown className="w-4 h-4" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Last Contact
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">Next Follow Up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {sortedContacts.map((contact) => (
              <tr 
                key={contact.id}
                className="hover:bg-dark-700/50 transition-colors group"
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(contact.id)}
                    onChange={() => toggleSelect(contact.id)}
                    className="rounded bg-dark-600 border-dark-500"
                  />
                </td>
                <td className="px-6 py-4">
                  <Link 
                    to={`/contacts/${contact.id}`}
                    state={{ from: location.pathname }}
                    className="hover:text-primary-500 transition-colors"
                  >
                    {settingsLoading ? (
                      <Skeleton className="h-5 w-32" />
                    ) : (
                      <div>
                        {useAnonymization(contact.first_name, 'name', shouldAnonymize)}{' '}
                        {useAnonymization(contact.last_name, 'name', shouldAnonymize)}
                        {contact.job_title && (
                          <div className="text-sm text-gray-400">
                            {contact.job_title}
                          </div>
                        )}
                      </div>
                    )}
                  </Link>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {contact.work_email && (
                      <div className="flex items-center text-sm text-gray-400">
                        <Mail className="w-4 h-4 mr-2" />
                        {settingsLoading ? (
                          <Skeleton className="h-4 w-48" />
                        ) : (
                          useAnonymization(contact.work_email, 'email', shouldAnonymize)
                        )}
                      </div>
                    )}
                    {contact.personal_email && (
                      <div className="flex items-center text-sm text-gray-400">
                        <Mail className="w-4 h-4 mr-2" />
                        {settingsLoading ? (
                          <Skeleton className="h-4 w-48" />
                        ) : (
                          useAnonymization(contact.personal_email, 'email', shouldAnonymize)
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center text-sm text-gray-400">
                    <Calendar className="w-4 h-4 mr-2" />
                    <LastContactDate 
                      date={contact.last_contact_date} 
                      type={contact.last_contact_type}
                    />
                  </div>
                </td>
                <td className="px-6 py-4">
                  <NextFollowUp contactId={contact.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDeleteConfirm && (
        <ConfirmationPopup
          title="Delete Contacts"
          message={`Are you sure you want to delete ${selectedContacts.size} contact(s)? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showAssignCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-dark-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Assign Company</h2>
            <p className="text-gray-400 mb-4">
              Select a company to assign to {selectedContacts.size} contact(s)
            </p>
            
            <div className="space-y-4">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="input w-full"
              >
                <option value="">Select company...</option>
                {companyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAssignCompany(false);
                    setSelectedCompany('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignCompany}
                  disabled={!selectedCompany}
                  className="btn btn-primary"
                >
                  Assign Company
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}